import { SvgHappyCloud } from "../Svgs/HappyCloud";
import { SvgImage } from "../Svgs/Image";
import { SvgSadCloud } from "../Svgs/SadCloud";
import { Body2, H4 } from "../Text";
import { useState } from "react";
import { FileError, useDropzone } from "react-dropzone";
import styled from "styled-components";
import { checkAndResizeImage } from "./checkAndResizeImage";
import { SvgErrorAlt } from "../Svgs/ErrorAlt";

const CONTENT = {
  inactive: {
    message: "Drop your image here",
    icon: <SvgImage style={{ width: 64, height: 64, opacity: 0.4 }} />,
  },
  active: {
    valid: {
      message: "Drop It!",
      icon: <SvgHappyCloud style={{ width: 64, height: 64 }} />,
    },
    invalid: {
      message: "Are you sure this is an image?",
      icon: <SvgSadCloud style={{ width: 64, height: 64 }} />,
    },
  },
};

const bytesToMegabytes = (bytes: number) => {
  const megabytes = bytes / (1024 * 1024);
  return megabytes.toFixed(megabytes % 1 === 0 ? 0 : 1);
};

const getErrorMessage = (e: FileError, maxSize: number) => {
  switch (e.code) {
    case "too-many-files":
      return "Too many files selected. Please upload only one file.";
    case "file-invalid-type":
      return "File type is not supported";
    case "file-too-large":
      return (
        "File is too large. Maximum size is " + bytesToMegabytes(maxSize) + "MB"
      );
    default:
      return e.message;
  }
};

interface ImageSelectProps {
  dropzoneDescription?: string;
  withBackground?: boolean;
  onSelect: (url: string, type: string) => void;
  maxSize?: number;
  maxFiles?: number;
  maxDimensions?: [number, number];
}

const defaultMaxSize = 25 * 1024 * 1024; // 25MB

export const ImageSelect = ({
  dropzoneDescription = "Accepted Formats: JPEG, PNG, WEBP",
  withBackground = true,
  onSelect,
  maxSize = defaultMaxSize,
  maxDimensions,
}: ImageSelectProps) => {
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);
  const { getRootProps, getInputProps, isDragActive, isDragAccept } =
    useDropzone({
      accept: {
        "image/png": [".png"],
        "image/jpeg": [".jpg", ".jpeg"],
        "image/webp": [".webp"],
      },
      onDrop: async ([file]) => {
        setErrors([]);
        if (file) {
          setIsResizing(true);
          try {
            const image = await checkAndResizeImage(file, maxDimensions);
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.onload = () => {
              onSelect(reader.result as string, image.type);
            };
          } catch (error) {
            console.error(error);
            setErrors(["An error occurred while processing the image"]);
          } finally {
            setIsResizing(false);
          }
        }
      },
      onDropRejected: (fileRejections) => {
        const errors = fileRejections.map(({ errors }) =>
          errors.map((e) => getErrorMessage(e, maxSize)),
        );
        setErrors(errors.flat());
      },
      maxSize,
      maxFiles: 1,
    });

  const content = !isDragActive
    ? CONTENT.inactive
    : CONTENT.active[isDragAccept ? "valid" : "invalid"];

  const contentUI = (
    <div
      style={{
        marginTop: 10,
        padding: "64px 20px",
        borderRadius: 6,
        background: withBackground
          ? "var(--content-background-color)"
          : undefined,
      }}
    >
      {isResizing ? (
        <H4>Optimizing image...</H4>
      ) : (
        <>
          <div style={{ margin: "-10px auto 0 auto" }}>{content.icon}</div>
          <div style={{ marginTop: 10, fontWeight: 600 }}>
            <span>{content.message}</span>
            {!isDragActive && (
              <span>
                , or{" "}
                <span
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Browse
                </span>
              </span>
            )}
            <div style={{ margin: "10px auto 0" }}>
              <Body2>{dropzoneDescription}</Body2>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div>
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        <div style={{ textAlign: "center" }}>{contentUI}</div>
      </div>
      {!!errors?.length && (
        <ImageUploadErrorContainer>
          {errors.map((error, index) => (
            <div key={`error-${index}`}>
              <SvgErrorAlt />
              <Body2>{error}</Body2>
            </div>
          ))}
        </ImageUploadErrorContainer>
      )}
    </div>
  );
};

export const ImageUploadErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;

  & > div {
    color: ${({ theme }) => theme.colors.error};
    display: flex;
    align-items: center;
    gap: 8px;
    ${Body2} {
      font-weight: 500;
    }

    &:first-child {
      margin-top: 0;
    }
  }
`;
